// @snippet

/**
 * @class LazySection
 */
import { mobile } from './constants';
import { loadLazySectionJSChunk } from './LazySectionChunks';

class LazySection {
  constructor(observer, stats) {
    this.observer = observer;
    this.stats = stats;
    this.loadingStylesheets = {};

    if (this.observer) {
      this.lazySectionsObserver();
      this.loadSectionsObserver();

      window.loadSection = this.loadSectionsObserver.bind(this);
    } else {
      this.lazySectionsStandard();
      this.loadSectionsStandard();
    }
  }

  async lazySectionsObserver() {
    this.observer.subscribe('.lazy-section', async (item) => {
      await this.loadItem(item);
      item.classList.remove('section-preloader');
      return true;
    });
  }

  async lazySectionsStandard() {
    const promises = [...document.querySelectorAll('.lazy-section')].map(
      async (item) => {
        await this.loadItem(item);
        return true;
      }
    );
    await Promise.all(promises);
  }

  async loadSectionsObserver(
    elements = '.section-preloader:not(.lazy-section)'
  ) {
    this.observer.subscribe(elements, async (item) => {
      await this.loadItem(item, false);
      item.classList.remove('section-preloader');
      return true;
    });
  }

  async loadSectionsStandard() {
    const promises = [
      ...document.querySelectorAll('.section-preloader:not(.lazy-section)'),
    ].map(async (item) => {
      await this.loadItem(item, false);
      item.classList.remove('section-preloader');
      item.classList.add('section-loaded');
      return true;
    });
    await Promise.all(promises);
  }

  async loadItem(item, html_load = true) {
    const wrapper = item.closest('.shopify-section');
    const css = wrapper.querySelectorAll('.chunk_css');
    const js = wrapper.querySelectorAll('.chunk_js');
    let html = false;

    if (css.length) {
      await this.loadCSS([...css]);
    }
    if (html_load) {
      html = await this.loadHTML(item);
    }
    if ((html && html_load && js.length) || (!html_load && js.length)) {
      await this.loadJS([...js], wrapper);
    }
    // resolve();
  }

  async loadHTML(item) {
    const $_section = item.closest('.shopify-section');
    const id = $_section.getAttribute('id').replace('shopify-section-', '');
    const url =
      document.location.origin +
      document.location.pathname +
      '?section_id=' +
      id +
      '&content=1';
    if (url && id) {
      const req = await fetch(url);
      let html = await req.text();
      html = new DOMParser().parseFromString(html, 'text/html');
      html = html.querySelector('body > div');
      if (!html) {
        return false;
      }
      $_section.innerHTML = html.innerHTML;

      //run script from HTML (if exist) - only scripts with id to avoid duplicates and infinite loops
      [...$_section.querySelectorAll('script')]
        .filter((script) => script?.id.includes('init'))
        .forEach((oldScript) => {
          const newScript = document.createElement('script');
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });

      //  run global inits for sections (if exist)
      window.SectionInits.forEach((fn) => {
        if (typeof fn === 'function') {
          fn();
        }
      });

      // clear global inits to avoid duplicates in next sections
      window.SectionInits = [];

      $_section.classList.add('html-loaded');

      if (
        $_section.querySelector('.toogleFilters_button') &&
        window.toogleCollapseFilter
      ) {
        window.toogleCollapseFilter();
      }
      if (window.LazyLoaderRun) {
        window.LazyLoaderRun();
      }

      // if (window.dy_eventsView && $_section.querySelector('.dy-custom-event--view')) {
      //     window.dy_eventsView($_section.querySelector('.dy-custom-event--view'))
      // }
      const elementEventView = $_section.querySelector(
        '.dy-custom-event--view'
      );
      if (
        window?.gld_dy?.enabled === true &&
        window.gld_dy.event &&
        elementEventView &&
        !elementEventView.classList.contains('dy-custom-event--done')
      ) {
        if (window.gld_dy.event(elementEventView)) {
          elementEventView.classList.add('dy-custom-event--done');
        }
      }
      // if (this.stats.listenerOnElements) {
      //     this.stats.listenerOnElements($_section)
      // }

      return html;
    } else {
      if (window.LazyLoaderRun) {
        window.LazyLoaderRun();
      }
    }
    return false;
  }

  async loadCSS(items) {
    const hrefs = this.datasetList(items, 'hrefs');
    if (hrefs.length) {
      const cssLoaded = await this.loadStylesheetHrefs(hrefs);
      if (cssLoaded) {
        return;
      }
    }

    let chunks = this.datasetList(items, 'chunks');
    const paths = chunks
      .filter((item, index) => chunks.indexOf(item) === index)
      .filter((item) => {
        return !this.isStylesheetLoaded(item.replace('/', '-'));
      });
    if (paths.length) {
      // for (const path of paths) {
      const promises = paths.map(async (path) => {
        await import(
          /* webpackChunkName: "[request]"*/ `../sass2/chunks/${path.trim()}.scss`
        );
      });
      await Promise.all(promises);
      // item.classList.add('css-loaded')
    }
  }

  datasetList(items, key) {
    let values = [];
    items.forEach((item) =>
      item?.dataset?.[key]
        ? (values = [].concat(values, item.dataset[key].split(',')))
        : false
    );

    return values
      .map((item) => item.trim())
      .filter((item) => item)
      .filter((item, index, arr) => arr.indexOf(item) === index);
  }

  async loadStylesheetHrefs(hrefs) {
    const promises = hrefs.map((href) => this.loadStylesheet(href));
    const results = await Promise.all(promises);
    return results.every((loaded) => loaded);
  }

  loadStylesheet(href) {
    const normalizedHref = this.normalizeAssetHref(href);
    const assetKey = this.assetFilename(normalizedHref);

    if (!normalizedHref || this.isStylesheetLoaded(normalizedHref)) {
      return Promise.resolve(true);
    }

    if (this.loadingStylesheets[assetKey]) {
      return this.loadingStylesheets[assetKey];
    }

    this.loadingStylesheets[assetKey] = new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = normalizedHref;
      link.dataset.sectionAsset = assetKey;
      link.onload = () => resolve(true);
      link.onerror = () => {
        console.warn(`Lazy section CSS failed to load: ${normalizedHref}`);
        resolve(false);
      };
      document.head.appendChild(link);
    });

    return this.loadingStylesheets[assetKey];
  }

  normalizeAssetHref(href) {
    if (!href) {
      return '';
    }

    if (href.indexOf('//') === 0) {
      return `${window.location.protocol}${href}`;
    }

    return href;
  }

  assetFilename(href) {
    return href.split('/').slice(-1)[0].split('?')[0];
  }

  isStylesheetLoaded(href) {
    const filename = this.assetFilename(href);

    return [...document.querySelectorAll('link[href]')].some((link) => {
      const linkFilename = this.assetFilename(link.href);
      return linkFilename === filename || linkFilename.indexOf(filename) !== -1;
    });
  }

  async loadJS(items, wrapper) {
    const deviceRegex = new RegExp(/\[(.*?)\]/);
    const paths = this.datasetList(items, 'chunks');
    if (paths.length) {
      for (let path of paths) {
        const regexExec = path.match(deviceRegex);
        const device = regexExec && regexExec[1] ? regexExec[1] : 'all';
        if (
          (device === 'desktop' && mobile) ||
          (device === 'mobile' && !mobile)
        ) {
          continue;
        } else if (regexExec && regexExec[0]) {
          path = path.replace(regexExec[0], '');
        }
        path = path.trim();

        await loadLazySectionJSChunk(path, {
          wrapper,
          loadSlider: this.loadSlider.bind(this),
        });
      }
      // item.classList.add('js-loaded')
    }
  }

  async loadSlider(item) {
    const path = 'plugins/swiper';
    await import(
      /* webpackChunkName: "plugins-swiper*/ `../sass2/chunks/${path.trim()}.scss`
    );
    const { default: Slider } = await import(
      /* webpackChunkName: "helpers_slider" */ `./helpers/Slider.js`
    );
    Slider.load(item);
  }
}
export default LazySection;
